import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import useQueries from '@/hooks/useQueries';
import { API_URL } from '@/shared/utils/API_URL';
import { buildQuery } from '@/shared/utils/buildQuery';

export interface AppUser {
    user_id: string;
    user_name: string;
    category_id: string | number | null;
}

export interface Category {
    category_id: string | number;
    category_name: string;
}

export interface CategoryPrivilege {
    privilegeid: string;
    privilegedesc: string;
    privilegeval: string;
}


const useAssignUserPrivilegesController = () => {
    const { t } = useTranslation();
    const queries = useQueries();
    const queryClient = useQueryClient();
    const sessionId = Cookies.get('sessionId');
    const licenceId = localStorage.getItem('licence_id');

    const [editingUserId, setEditingUserId] = useState<string | null>(null);
    const [draftCategoryId, setDraftCategoryId] = useState<string>('');


    const postQuery = async (q: string, t_param: 'sel' | 'in' = 'sel') => {
        const res = await axios.post(
            API_URL,
            { q },
            { params: { t: t_param, id: licenceId, s: sessionId, btn: 'w_itm', api: 'itqan' } }
        );
        return res?.data?.data?.result;
    };

    const { data: categories = [] } = useQuery<Category[]>({
        queryKey: ['allCategories', queries?.allCategoriesQuery],
        queryFn: () => postQuery(queries!.allCategoriesQuery),
        enabled: !!queries?.allCategoriesQuery,
    });

    const { data: users = [], isLoading: isLoadingUsers } = useQuery<AppUser[]>({
        queryKey: ['usersWithCategory', queries?.usersWithCategoryQuery],
        queryFn: () => postQuery(queries!.usersWithCategoryQuery),
        enabled: !!queries?.usersWithCategoryQuery,
    });

    const { data: previewPrivileges = [] } = useQuery<CategoryPrivilege[]>({
        queryKey: ['categoryPrivileges', draftCategoryId],
        queryFn: () =>
            postQuery(buildQuery(queries!.categoryPrivilegesQuery, { category_id: draftCategoryId })),
        enabled: !!queries?.categoryPrivilegesQuery && !!draftCategoryId && !!editingUserId,
    });

    const startEditing = (user: AppUser) => {
        setEditingUserId(user.user_id);
        setDraftCategoryId(user.category_id ? String(user.category_id) : '');
    };

    const cancelEditing = () => {
        setEditingUserId(null);
        setDraftCategoryId('');
    };

    const mutation = useMutation({
        mutationFn: async (userId: string) => {
            const updateQ = buildQuery(queries!.updateUserCategoryQuery, {
                user_id: userId,
                category_id: draftCategoryId,
            });
            await postQuery(updateQ, 'in');
        },
        onSuccess: () => {
            toast.success(t('Role updated successfully!'));
            queryClient.invalidateQueries({ queryKey: ['usersWithCategory'] });
            setEditingUserId(null);
        },
        onError: (error: any) => {
            toast.error(error?.message || t('Error updating category'));
        },
    });


    return {
        t,
        users,
        isLoadingUsers,
        categories,
        previewPrivileges,
        editingUserId,
        draftCategoryId,
        setDraftCategoryId,
        isSaving: mutation.isPending,
        startEditing,
        cancelEditing,
        saveCategory: (userId: string) => mutation.mutate(userId),
    };
};

export default useAssignUserPrivilegesController;